import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, HardHat } from "lucide-react";
import { toast } from "sonner";

export default function QuoteReply() {
  const { token } = useParams();
  const [q, setQ] = useState(undefined);
  const [form, setForm] = useState({ price: "", contractor_name: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/quote/${token}`).then(({ data }) => {
      setQ(data);
      if (data.status === "quoted") setDone(true);
    }).catch(() => setQ(null));
  }, [token]);

  const submit = async () => {
    if (!form.price || Number(form.price) <= 0) return toast.error("Enter your price.");
    setSaving(true);
    try {
      await api.post(`/public/quote/${token}`, { price: Number(form.price), contractor_name: form.contractor_name, note: form.note });
      setDone(true);
    } catch {
      toast.error("Could not submit your quote.");
    } finally {
      setSaving(false);
    }
  };

  if (q === undefined) return <div className="min-h-screen bp-grid"><Navbar /><div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div></div>;
  if (q === null) return (
    <div className="min-h-screen bp-grid"><Navbar />
      <div className="mx-auto max-w-md px-4 py-28 text-center">
        <HardHat className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 font-heading text-2xl font-bold">Quote request not found</h1>
        <p className="mt-2 text-muted-foreground">This link may have expired.</p>
      </div>
    </div>
  );

  const sym = q.currency_symbol || "$";
  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-lg px-4 py-16 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary"><HardHat className="mr-2 inline h-3.5 w-3.5" /> Request for quote</p>
        <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tighter">{q.project_name}</h1>
        <p className="mt-2 text-muted-foreground">You've been asked to quote this scope of work. The full PDF was attached in the email.</p>
        {q.our_estimate != null && (
          <p className="mt-3 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Reference estimate: <span className="text-primary">{money(q.our_estimate, sym)} {q.currency}</span></p>
        )}
        {q.message && <p className="mt-4 border-l-2 border-primary bg-secondary/10 p-4 text-sm text-muted-foreground">"{q.message}"</p>}

        {done ? (
          <div className="mt-8 border border-primary/50 bg-secondary/10 p-8 text-center" data-testid="quote-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
            <h2 className="mt-4 font-heading text-2xl font-bold">Quote submitted</h2>
            <p className="mt-2 text-muted-foreground">Thanks{q.contractor_name ? `, ${q.contractor_name}` : ""} — your price has been shared.</p>
            {q.price != null && <p className="mt-3 font-heading text-3xl font-extrabold text-primary">{money(q.price, sym)}</p>}
          </div>
        ) : (
          <div className="mt-8 space-y-4 border border-border p-6" data-testid="quote-form">
            <div>
              <Label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Your price ({q.currency})</Label>
              <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="quote-price"
                placeholder="0" className="mt-1.5 rounded-sm border-border bg-transparent" />
            </div>
            <div>
              <Label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Your name / company</Label>
              <Input value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} data-testid="quote-name"
                className="mt-1.5 rounded-sm border-border bg-transparent" />
            </div>
            <div>
              <Label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Note (optional)</Label>
              <Textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="quote-note"
                className="mt-1.5 rounded-sm border-border bg-transparent" placeholder="Timeline, inclusions, assumptions…" />
            </div>
            <Button onClick={submit} disabled={saving} data-testid="quote-submit"
              className="w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Submit Quote
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
